import { useEffect, useState } from "react";
import { Loader2, Filter, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  getHistoryDetail,
  listHistory,
  listProjects,
  type AuditDetail,
  type AuditRow,
  type ProjectSummary,
} from "@/lib/api";

export function HistoryPage() {
  const [rows, setRows] = useState<AuditRow[] | null>(null);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [filter, setFilter] = useState<{
    project?: string;
    decision?: "allowed" | "denied";
  }>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AuditDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    try {
      const [r, p] = await Promise.all([listHistory(filter), listProjects()]);
      setRows(r);
      setProjects(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.project, filter.decision]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setDetailError(null);
      return;
    }

    let cancelled = false;
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    void getHistoryDetail(selectedId)
      .then((row) => {
        if (!cancelled) setDetail(row);
      })
      .catch((e) => {
        if (!cancelled) {
          setDetailError(e instanceof Error ? e.message : "Failed to load run");
        }
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  return (
    <div className="container py-6">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold">History</h1>
          <p className="text-sm text-muted-foreground">
            Audit log: every trigger attempt, allowed or denied.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          <Button
            size="sm"
            variant={!filter.project ? "default" : "outline"}
            onClick={() => setFilter((f) => ({ ...f, project: undefined }))}
          >
            All projects
          </Button>
          {projects.map((p) => (
            <Button
              key={p.id}
              size="sm"
              variant={filter.project === p.id ? "default" : "outline"}
              onClick={() => setFilter((f) => ({ ...f, project: p.id }))}
            >
              <span className="mr-1" aria-hidden>{p.icon}</span>
              {p.name}
            </Button>
          ))}
          <span className="mx-2 text-muted-foreground">·</span>
          {(["allowed", "denied"] as const).map((d) => (
            <Button
              key={d}
              size="sm"
              variant={filter.decision === d ? "default" : "outline"}
              onClick={() =>
                setFilter((f) => ({
                  ...f,
                  decision: f.decision === d ? undefined : d,
                }))
              }
            >
              {d}
            </Button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {!rows && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {rows && rows.length === 0 && (
        <p className="text-sm text-muted-foreground">No matching runs.</p>
      )}

      {rows && rows.length > 0 && (
        <div className="rounded-lg border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2">Time</th>
                <th className="px-4 py-2">Decision</th>
                <th className="px-4 py-2">Project</th>
                <th className="px-4 py-2">Task</th>
                <th className="px-4 py-2">Tier</th>
                <th className="px-4 py-2">Actor</th>
                <th className="px-4 py-2">Runner</th>
                <th className="px-4 py-2">Run ID</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setSelectedId(r.id)}
                  className={
                    "cursor-pointer border-b transition-colors last:border-0 hover:bg-muted/50 " +
                    (r.god_session_id ? "bg-accent/10" : "")
                  }
                >
                  <td className="whitespace-nowrap px-4 py-2 text-xs text-muted-foreground">
                    {new Date(r.ts).toLocaleString()}
                  </td>
                  <td className="px-4 py-2">
                    <Badge
                      variant={
                        r.decision === "allowed" ? "success" : "destructive"
                      }
                    >
                      {r.decision}
                    </Badge>
                    {r.god_session_id && (
                      <span className="ml-1" title="god session">
                        🔓
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs">{r.project ?? "—"}</td>
                  <td className="px-4 py-2 font-mono text-xs">
                    {r.task_id ?? "—"}
                  </td>
                  <td className="px-4 py-2">
                    {r.tier && <Badge variant="outline">{r.tier}</Badge>}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {r.actor_email ?? r.actor_id}
                  </td>
                  <td className="px-4 py-2 text-xs">{r.runner_id ?? "—"}</td>
                  <td className="px-4 py-2 font-mono text-xs">
                    <span className="inline-flex items-center gap-1">
                      {r.run_id?.slice(0, 14) ?? r.denial_reason ?? "—"}
                      <ExternalLink className="h-3 w-3 text-muted-foreground" />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        open={Boolean(selectedId)}
        onOpenChange={(open) => !open && setSelectedId(null)}
      >
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-hidden p-0">
          <DialogHeader className="border-b px-6 py-4">
            <DialogTitle>Run Detail</DialogTitle>
            <DialogDescription>
              Payload, Trigger.dev status, output, and error for this history row.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[calc(90vh-90px)] overflow-auto p-6">
            {detailLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading run detail…
              </div>
            )}
            {detailError && (
              <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {detailError}
              </div>
            )}
            {detail && <HistoryDetailPanel detail={detail} />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HistoryDetailPanel({ detail }: { detail: AuditDetail }) {
  const result = detail.result_summary;
  return (
    <div className="space-y-4 text-sm">
      <div className="grid gap-3 md:grid-cols-3">
        <DetailMetric label="Decision" value={detail.decision} />
        <DetailMetric label="Status" value={detail.run?.status ?? "no run"} />
        <DetailMetric
          label="Duration"
          value={
            typeof detail.run?.durationMs === "number"
              ? `${detail.run.durationMs}ms`
              : "—"
          }
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <JsonBlock title="Payload" value={detail.payload} />
        <JsonBlock
          title="Output"
          value={detail.run?.output ?? result?.output ?? null}
          empty="No output recorded yet."
        />
      </div>

      {(detail.run?.error ?? result?.error ?? detail.denial_reason) && (
        <JsonBlock
          title="Error"
          value={detail.run?.error ?? result?.error ?? detail.denial_reason}
          tone="destructive"
        />
      )}

      <JsonBlock
        title="Run Metadata"
        value={{
          id: detail.run_id,
          task: detail.task_id,
          project: detail.project,
          actor: detail.actor_email ?? detail.actor_id,
          runner: detail.runner_id,
          sourceIp: detail.source_ip,
          timestamp: detail.ts,
          trigger: detail.run,
        }}
      />
    </div>
  );
}

function DetailMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-muted/20 px-3 py-2">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 font-mono text-xs">{value}</div>
    </div>
  );
}

function JsonBlock({
  title,
  value,
  empty = "—",
  tone,
}: {
  title: string;
  value: unknown;
  empty?: string;
  tone?: "destructive";
}) {
  const hasValue = value !== null && value !== undefined;
  return (
    <section>
      <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </h2>
      <pre
        className={
          "max-h-[360px] overflow-auto rounded-md border p-3 font-mono text-xs " +
          (tone === "destructive"
            ? "border-destructive/40 bg-destructive/10 text-destructive"
            : "bg-muted")
        }
      >
        {hasValue ? JSON.stringify(value, null, 2) : empty}
      </pre>
    </section>
  );
}
